import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import { articles } from '@/mocks/articles';
import { articleImages } from '@/mocks/articleImages';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Button from '@/components/base/Button';

function buildArticleSchema(article: typeof articles[0], slug: string, imageUrl: string) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuypolska.lat';
  const articleUrl = `${siteUrl}/artykul/${slug}`;

  const faqSchema = article.faqs && article.faqs.length > 0 ? {
    "@type": "FAQPage",
    "mainEntity": article.faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  } : null;

  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt,
    "image": imageUrl,
    "datePublished": "2026-07-01",
    "dateModified": "2026-07-20",
    "author": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "Kakobuy Spreadsheet Polska",
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    },
    ...(faqSchema ? { "hasPart": faqSchema } : {})
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-[60vh] flex items-center justify-center">
          <div className="text-center px-4">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">Artykuł nie został znaleziony</h1>
            <p className="text-sm text-foreground-500 mb-6">Przepraszamy, ale artykuł o podanym adresie nie istnieje.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium text-sm cursor-pointer">
              <i className="ri-arrow-left-line"></i> Powrót do strony głównej
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const heroImage = articleImages[article.slug] || articleImages["kakobuy-spreadsheet-poradnik"];
  const schemaData = buildArticleSchema(article, slug || '', heroImage);

  return (
    <>
      <Navbar />
      <main className="pt-20 md:pt-24">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6">
            <Link to="/" className="hover:text-primary-500 transition-colors">Strona główna</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-400">{article.title}</span>
          </nav>

          <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4 md:mb-6 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-3 text-xs text-foreground-400 mb-8">
            <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
            <span>·</span>
            <span><i className="ri-time-line mr-1"></i>{article.readTime} czytania</span>
            <span>·</span>
            <span><i className="ri-user-line mr-1"></i>Zespół Kakobuy Spreadsheet Polska</span>
          </div>

          <div className="relative w-full h-[260px] md:h-[400px] rounded-lg overflow-hidden mb-8 md:mb-10">
            <img
              src={heroImage}
              alt={article.title}
              className="w-full h-full object-cover object-center"
            />
          </div>

          <div className="prose-custom text-foreground-800 text-sm md:text-base leading-relaxed space-y-6">
            <p className="text-lg md:text-xl font-medium text-foreground-900">
              {article.excerpt}
            </p>

            {article.content.map((block, idx) => {
              if (block.type === 'h2') {
                return (
                  <h2 key={idx} className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
                    {block.text}
                  </h2>
                );
              }
              if (block.type === 'p') {
                return (
                  <p key={idx} dangerouslySetInnerHTML={{ __html: block.text }} />
                );
              }
              return null;
            })}

            {article.faqs && article.faqs.length > 0 && (
              <>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
                  Najczęściej zadawane pytania (FAQ)
                </h2>
                <div className="space-y-4">
                  {article.faqs.map((faq, idx) => (
                    <div key={idx} className="bg-background-50 rounded-lg p-5 border border-background-200">
                      <h3 className="font-semibold text-foreground-900 mb-2">{faq.q}</h3>
                      <p className="text-sm text-foreground-600">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </>
            )}

            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-10 mb-4">
              Podsumowanie
            </h2>
            <p>
              Mamy nadzieję, że ten artykuł pomógł Ci lepiej zrozumieć temat. <strong>Kakobuy Spreadsheet</strong> to potężne narzędzie, które zmienia sposób w jaki robimy zakupy z Chin.
            </p>
            <p>
              Sprawdź nasze pozostałe <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium">artykuły i poradniki</Link>, aby pogłębić swoją wiedzę o zakupach z Kakobuy!
            </p>
          </div>

          <div className="mt-12 p-6 md:p-8 bg-background-50 rounded-lg border border-background-200 text-center">
            <h3 className="text-lg md:text-xl font-heading font-semibold text-foreground-900 mb-3">
              Gotowy, aby zacząć zakupy?
            </h3>
            <p className="text-sm text-foreground-600 mb-5">
              Otwórz Kakobuy Spreadsheet i odkryj najlepsze produkty z Chin już teraz.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button variant="primary" href="https://kaspreadsheet.com">
                Otwórz Kakobuy Spreadsheet
              </Button>
              <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
                <i className="ri-arrow-left-line mr-1"></i> Powrót do strony głównej
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}